import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Konva applies fit-to-container scaling on the Layer and interactive zoom on
 * the Stage. Annotation chrome must compensate for both transforms to remain
 * a usable, screen-constant size on high-resolution captures.
 */
export const computeTotalDisplayScale = (initialScale: number, stageZoom: number) => initialScale * stageZoom;

/**
 * The image-space rectangle currently on screen.
 *
 * Chrome that must stay READABLE — as opposed to merely correctly placed — has to
 * be positioned against this rather than against the image bounds: the Stage is
 * clipped to its container, so once the user zooms in, most of the image is off
 * screen and an "inside the image" guarantee says nothing about visibility.
 */
export const computeVisibleImageRect = (
  position: { x: number; y: number },
  stageZoom: number,
  fitScale: number,
  viewport: { width: number; height: number }
) => {
  const displayScale = computeTotalDisplayScale(fitScale, stageZoom);
  return {
    // `|| 0` normalizes the -0 that a zero offset produces, so callers comparing
    // rects (and tests) see a plain 0.
    x: -position.x / displayScale || 0,
    y: -position.y / displayScale || 0,
    width: viewport.width / displayScale,
    height: viewport.height / displayScale,
  };
};

/**
 * Chrome growth saturates here. Hand-zooming tops out around this stage zoom (the keyboard path caps
 * at 8), but review-mode box focus routinely reaches 10-30 on high-res captures — where an unbounded
 * linear growth term would render outlines and handles several times their intended thickness.
 */
const MAX_CHROME_GROWTH_ZOOM = 8;

/**
 * Konva chrome (lines, dots, outlines) is drawn in Layer coordinates, so a fixed value shrinks by the
 * fit-to-container scale on high-res captures and reads as hair-thin. This returns the Layer-coordinate
 * size that renders at ~`basePx` on screen and grows *slightly* as the user zooms the stage in
 * (`growth` = extra fraction of `basePx` per +1× of stage zoom, saturating at
 * `MAX_CHROME_GROWTH_ZOOM`). Inverse of `computeTotalDisplayScale`.
 */
export const zoomAdjustedLayerSize = (basePx: number, fitScale: number, stageZoom: number, growth = 0.12) =>
  (basePx * (1 + growth * Math.max(0, Math.min(stageZoom, MAX_CHROME_GROWTH_ZOOM) - 1))) /
  computeTotalDisplayScale(fitScale, stageZoom);

export type KeyboardZoomDirection = "in" | "out";

/** One keyboard zoom step: multiply/divide the current stage scale by `factor`, clamped to `[min, max]`. */
export const computeKeyboardZoom = (
  current: number,
  direction: KeyboardZoomDirection,
  factor: number,
  min: number,
  max: number,
) => {
  const next = direction === "in" ? current * factor : current / factor;
  return Math.min(max, Math.max(min, next));
};

/**
 * Wheel-button (middle-mouse) press-drag zoom: turn a vertical drag delta (screen px,
 * positive when the mouse moves forward/up) into a new stage scale. Exponential so the
 * feel is uniform at any zoom, and multiplicative so chunked mousemove deltas compose to
 * the same result as one continuous drag. Clamped to `min`; upper bound is left to the
 * caller (the wheel/pinch path is unbounded too).
 */
export const computeWheelButtonDragZoom = (
  current: number,
  deltaUpPx: number,
  sensitivity: number,
  min: number,
) => Math.max(min, current * Math.exp(deltaUpPx * sensitivity));

/** Fraction of the viewport left clear on EACH side of a focused box (so it fills ~92%). */
export const FOCUS_PADDING_FRACTION = 0.04;

/**
 * Ceiling for a programmatic focus, in ON-SCREEN PIXELS PER IMAGE PIXEL rather than stage zoom.
 * Stage zoom alone is meaningless without the fit scale: on a 4032×3024 capture the fit scale is
 * ~0.27, so stage zoom 8 is only ~2× native, while on a 640px image it would be ~15×. 8 px/px is
 * the familiar "800%" stop — past it a JPEG is interpolation, not detail.
 */
export const MAX_FOCUS_DISPLAY_SCALE = 8;

/** An absolute Stage transform: the Stage's own zoom plus its position, in container pixels. */
export interface StageView {
  stageZoom: number;
  stagePosition: { x: number; y: number };
}

/** A one-shot request to jump the stage somewhere. See `AnnotationViewerBase`'s `focusRequest`. */
export interface StageFocusRequest {
  /** Image-pixel box to fill the viewport, or null to return to the whole-image fit. */
  bbox: { x: number; y: number; width: number; height: number } | null;
  /**
   * Bumped on EVERY request the caller wants honored — including a re-issue of the same box, and
   * two same-sized boxes in a row. The viewer keys its effect on this number alone, so an
   * unchanged token is deliberately ignored and the user keeps whatever view they panned to.
   */
  token: number;
}

/**
 * Keep the stage inside the image: no letterboxing when zoomed in, centered when the scaled image
 * is smaller than the viewport on that axis. `scaledImageSize` is the image at the Layer's fit scale
 * (i.e. at stage zoom 1), so the on-screen image measures `scaledImageSize * stageZoom`.
 *
 * Axes are independent — a wide image at zoom 2 can need x clamping while y still centers.
 */
export function clampStagePosition(
  position: { x: number; y: number },
  stageZoom: number,
  scaledImageSize: { width: number; height: number },
  viewportSize: { width: number; height: number }
): { x: number; y: number } {
  const scaledW = scaledImageSize.width * stageZoom;
  const scaledH = scaledImageSize.height * stageZoom;

  const x =
    scaledW <= viewportSize.width
      ? (viewportSize.width - scaledW) / 2
      : Math.min(0, Math.max(viewportSize.width - scaledW, position.x));

  const y =
    scaledH <= viewportSize.height
      ? (viewportSize.height - scaledH) / 2
      : Math.min(0, Math.max(viewportSize.height - scaledH, position.y));

  return { x, y };
}

/**
 * The Stage transform that makes `bbox` fill the viewport with `paddingFraction` of the viewport
 * clear on each side. The inverse of `computeNormalizedViewport`: that maps a stage transform to a
 * normalized center; this maps a desired image-space box back to a stage transform.
 *
 * `bbox` is in absolute image pixels, top-left origin, in the display-oriented space the Layer draws
 * in — i.e. exactly an `Annotation.bbox`.
 *
 * Guarantees:
 *  - Never zooms below fit (`stageZoom >= 1`), matching every other zoom path in the viewer.
 *  - Never exceeds `maxDisplayScale` on-screen pixels per image pixel.
 *  - The position is already clamped, so callers must not clamp again. Clamping means a box near an
 *    image edge CANNOT be centered — it ends up flush against that edge. It is still fully visible:
 *    the box spans at most `1 - 2*padding` of the viewport, so even a corner box leaves margin on
 *    the far side.
 *  - Returns null for degenerate geometry, so a NaN can never reach `stage.scale()`/`position()` —
 *    a NaN there blanks the canvas with no way to recover.
 */
export function computeFocusView(
  bbox: { x: number; y: number; width: number; height: number },
  fitScale: number,
  scaledImageSize: { width: number; height: number },
  viewportSize: { width: number; height: number },
  options?: { paddingFraction?: number; maxDisplayScale?: number }
): StageView | null {
  const padding = options?.paddingFraction ?? FOCUS_PADDING_FRACTION;
  const maxDisplayScale = options?.maxDisplayScale ?? MAX_FOCUS_DISPLAY_SCALE;

  if (![bbox.x, bbox.y, bbox.width, bbox.height, fitScale].every(Number.isFinite)) return null;
  if (fitScale <= 0) return null;
  if (viewportSize.width <= 0 || viewportSize.height <= 0) return null;
  if (scaledImageSize.width <= 0 || scaledImageSize.height <= 0) return null;

  // Tolerate a negative-extent box without producing a bogus center.
  const left = Math.min(bbox.x, bbox.x + bbox.width);
  const top = Math.min(bbox.y, bbox.y + bbox.height);
  const width = Math.abs(bbox.width);
  const height = Math.abs(bbox.height);

  // A zero-size box is a point: treat it as one image pixel so the fit stays finite. It then lands
  // at max zoom, centered on the point — the only sensible reading.
  const wantedDisplayScale = Math.min(
    (viewportSize.width * (1 - 2 * padding)) / Math.max(width, 1),
    (viewportSize.height * (1 - 2 * padding)) / Math.max(height, 1)
  );

  // Math.max LAST so the never-below-fit floor wins even on a tiny image whose fit scale already
  // exceeds maxDisplayScale (e.g. a 100px image blown up into a 1200px viewport).
  const stageZoom = Math.max(1, Math.min(wantedDisplayScale / fitScale, maxDisplayScale / fitScale));
  const displayScale = computeTotalDisplayScale(fitScale, stageZoom);

  return {
    stageZoom,
    stagePosition: clampStagePosition(
      {
        x: viewportSize.width / 2 - (left + width / 2) * displayScale,
        y: viewportSize.height / 2 - (top + height / 2) * displayScale,
      },
      stageZoom,
      scaledImageSize,
      viewportSize
    ),
  };
}

export interface AnnotationViewport {
  zoom: number;
  center: { x: number; y: number };
}

export function computeNormalizedViewport(
  stagePosition: { x: number; y: number },
  stageZoom: number,
  fitScale: number,
  imageSize: { width: number; height: number },
  viewportSize: { width: number; height: number }
): AnnotationViewport {
  const effectiveScale = Math.max(stageZoom * fitScale, Number.EPSILON);
  const imageWidth = Math.max(imageSize.width, 1);
  const imageHeight = Math.max(imageSize.height, 1);
  const centerX = (viewportSize.width / 2 - stagePosition.x) / effectiveScale / imageWidth;
  const centerY = (viewportSize.height / 2 - stagePosition.y) / effectiveScale / imageHeight;
  return {
    zoom: Math.max(stageZoom, 1),
    center: {
      x: Math.min(1, Math.max(0, centerX)),
      y: Math.min(1, Math.max(0, centerY)),
    },
  };
}

/**
 * Convert a container-relative pixel point (e.g. clientX/Y minus the stage
 * container's bounding rect) into image/Layer coordinates — the same space the
 * annotation bboxes and `getImagePoint` use. Inverse of the Stage position +
 * (fitScale × stageZoom) transform.
 *
 * With `clamp`, an out-of-bounds point is snapped to the nearest image edge so a
 * box drawn past the edge (or with the cursor off the canvas) keeps following the
 * pointer instead of being dropped. Without it, an out-of-bounds point returns
 * null (matching the in-bounds-only hover/select behavior).
 */
export function mapContainerPointToImage(
  containerPoint: { x: number; y: number },
  stagePosition: { x: number; y: number },
  stageZoom: number,
  fitScale: number,
  imageSize: { width: number; height: number },
  options?: { clamp?: boolean }
): { x: number; y: number } | null {
  const effectiveScale = computeTotalDisplayScale(fitScale, stageZoom);
  const x = (containerPoint.x - stagePosition.x) / effectiveScale;
  const y = (containerPoint.y - stagePosition.y) / effectiveScale;

  if (options?.clamp) {
    return {
      x: Math.max(0, Math.min(imageSize.width, x)),
      y: Math.max(0, Math.min(imageSize.height, y)),
    };
  }

  if (x < 0 || y < 0 || x > imageSize.width || y > imageSize.height) return null;
  return { x, y };
}

export const useSizeListener = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setSize({ width, height });
    });

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  return { containerRef, size };
};

export const useGetDimensions = (image: HTMLImageElement, stageWidth: number, stageHeight: number) => {
  return useMemo(() => {
    if (!image || stageWidth === 0 || stageHeight === 0) {
      return { scale: 1, scaledImageWidth: 0, scaledImageHeight: 0, offsetX: 0, offsetY: 0 };
    }

    const scale = Math.min(stageWidth / image.width, stageHeight / image.height);
    const scaledImageWidth = image.width * scale;
    const scaledImageHeight = image.height * scale;

    return {
      scale,
      scaledImageWidth,
      scaledImageHeight,
      offsetX: (stageWidth - scaledImageWidth) / 2,
      offsetY: (stageHeight - scaledImageHeight) / 2,
    };
  }, [image, stageWidth, stageHeight]);
};
